import { singularLabel } from './singularLabel';

/**
 * Wording for a facility's renamed meal pattern / adjustments.
 *
 * Facilities rename both (tblFacility.MealPatternShortDescription /
 * AdjustmentsShortDescription): "Standing Order", "Standing Orders",
 * "Standing Order/Meal Pattern". The value is free text, so some are
 * singular nouns and some already plural. WinForms always appends
 * "Items" ("Delete All {0} Items"), which turns "Standing Orders" into
 * "Standing Orders Items"; these helpers only add it when the label does
 * not already read as plural.
 */

/** "Standing Orders", "Adjustments", "Standing Items" - but not "Meal Pattern" or "Class". */
export function isPluralLabel(label: string): boolean {
  return singularLabel(label) !== label;
}

/**
 * The collection, for titles and empty states.
 * "Meal Pattern" -> "Meal Pattern Items", "Standing Orders" -> "Standing Orders",
 * "Adjustments" -> "Adjustments".
 */
export function itemsLabel(label: string): string {
  return isPluralLabel(label) ? label : `${label} Items`;
}

/**
 * One row, for "Delete ..." and "New ..." wording.
 * "Meal Pattern" -> "Meal Pattern Item", "Standing Items" -> "Standing Item",
 * "Adjustments" -> "Adjustment".
 */
export function itemLabel(label: string): string {
  return isPluralLabel(label) ? singularLabel(label) : `${label} Item`;
}

/** "a meal pattern item", "an adjustment". */
export function withArticle(phrase: string): string {
  return `${/^[aeiou]/i.test(phrase) ? 'an' : 'a'} ${phrase}`;
}

export interface PatternLabels {
  /** The facility's short description as entered: "Meal Pattern", "Standing Order". */
  name: string;
  /** The facility's long description: "Standing Order (Meal & Nourishments)". */
  longName: string;
  /** Collection wording: "Meal Pattern Items", "Adjustments". */
  items: string;
  /** Single-row wording: "Meal Pattern Item", "Adjustment". */
  item: string;
}

export function patternLabels(shortName: string, longName?: string): PatternLabels {
  const name = shortName.trim();
  return {
    name,
    longName: (longName ?? '').trim() || name,
    items: itemsLabel(name),
    item: itemLabel(name),
  };
}
